/**
 * Formatos dos registros da puericultura (Módulo 7): cadastro da criança ao iniciar o seguimento
 * (D-040), exames e consultas (D-041, D-042), encaminhamentos, cancelamento de item da agenda e
 * encerramento (D-044). Os mesmos schemas validam o formulário e a API.
 */
import { z } from 'zod';
import { CLASSIFICACOES_SEGUIMENTO } from '../protocolo/puericultura';
import { cnsSchema, cpfSchema } from './documentos';
import { hojeEmBrasilia } from './esquemas-cadastro';
import { SEXOS_RN } from './esquemas-maternidade';
import { EXAMES_NAO_TREPONEMICOS, EXAMES_TREPONEMICOS, TITULOS } from './esquemas-prenatal';

const dataPassada = (rotulo: string) =>
  z.iso
    .date({ message: 'Informe uma data válida.' })
    .refine((d) => d <= hojeEmBrasilia(), { message: `${rotulo} não pode estar no futuro.` });

const textoOpcional = (max = 2000) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const opcionalCom = <T extends z.ZodType>(schema: T) =>
  z.union([z.literal('').transform(() => null), z.null(), schema]).optional();

const numeroOpcional = (min: number, max: number, mensagem: string) =>
  z
    .union([z.number(), z.literal('').transform(() => null), z.null()])
    .optional()
    .refine((v) => v === null || v === undefined || (v >= min && v <= max), { message: mensagem });

const justificativa = (rotulo: string) =>
  z
    .string()
    .trim()
    .min(10, { message: `${rotulo} (mínimo de 10 caracteres).` })
    .max(1000);

// ---------------------------------------------------------------- iniciar o seguimento (D-040)
export const iniciarSeguimentoSchema = z.object({
  /** Nome definitivo; o formulário vem com o provisório "RN de <mãe>". */
  nome: z
    .string()
    .trim()
    .min(3, { message: 'Informe o nome da criança.' })
    .max(255, { message: 'Use no máximo 255 caracteres.' }),
  sexo: z.enum(SEXOS_RN, { message: 'Escolha o sexo.' }),
  cpf: opcionalCom(cpfSchema),
  cns: opcionalCom(cnsSchema),
  /** Unidade que acompanha; obrigatória só para quem não tem unidade fixa. */
  unidadeId: z.uuid({ message: 'Escolha a unidade que acompanha.' }).nullable().optional(),
  dataInicio: dataPassada('A data de início'),
});
export type DadosIniciarSeguimento = z.infer<typeof iniciarSeguimentoSchema>;

// ---------------------------------------------------------------- exames do seguimento
export const EXAMES_SEGUIMENTO = [...EXAMES_NAO_TREPONEMICOS, ...EXAMES_TREPONEMICOS] as const;

export const exameSeguimentoSchema = z
  .object({
    tipoExame: z.enum(EXAMES_SEGUIMENTO, { message: 'Escolha o exame.' }),
    dataColeta: dataPassada('A data da coleta'),
    resultado: z.enum(['reagente', 'nao_reagente', 'aguardando_resultado'], {
      message: 'Escolha o resultado.',
    }),
    titulo: z.enum(TITULOS).nullable().optional(),
    observacoes: textoOpcional(),
  })
  .refine(
    (e) =>
      !(EXAMES_NAO_TREPONEMICOS as readonly string[]).includes(e.tipoExame) ||
      e.resultado !== 'reagente' ||
      Boolean(e.titulo),
    { path: ['titulo'], message: 'Informe o título (ex.: 1:8).' },
  )
  .refine(
    (e) =>
      !e.titulo ||
      (e.resultado === 'reagente' &&
        (EXAMES_NAO_TREPONEMICOS as readonly string[]).includes(e.tipoExame)),
    { path: ['titulo'], message: 'Título só se aplica a VDRL/RPR reagente.' },
  );
export type DadosExameSeguimento = z.infer<typeof exameSeguimentoSchema>;

// ---------------------------------------------------------------- consulta (D-042)
export const consultaPuericulturaSchema = z.object({
  dataConsulta: dataPassada('A data da consulta'),
  pesoGramas: numeroOpcional(300, 40000, 'Peso entre 300 e 40.000 gramas.'),
  alturaCm: numeroOpcional(20, 150, 'Comprimento entre 20 e 150 cm.'),
  perimetroCefalicoCm: numeroOpcional(20, 60, 'Perímetro cefálico entre 20 e 60 cm.'),
  conduta: textoOpcional(),
  observacoes: textoOpcional(),
});
export type DadosConsultaPuericultura = z.infer<typeof consultaPuericulturaSchema>;

// ---------------------------------------------------------------- encaminhamentos (D-042)
/** Os mesmos valores do enum `tipo_encaminhamento_enum` do banco. */
export const TIPOS_ENCAMINHAMENTO = [
  'oftalmologia',
  'audiologia',
  'bera',
  'infectologia',
  'neurologia',
  'neuropediatria',
  'pediatria',
  'outro',
] as const;
export type TipoEncaminhamento = (typeof TIPOS_ENCAMINHAMENTO)[number];

export const ROTULO_ENCAMINHAMENTO: Record<TipoEncaminhamento, string> = {
  oftalmologia: 'Oftalmologia',
  audiologia: 'Audiologia',
  bera: 'BERA (potencial evocado auditivo)',
  infectologia: 'Infectologia',
  neurologia: 'Neurologia',
  neuropediatria: 'Neuropediatria',
  pediatria: 'Pediatria',
  outro: 'Outro',
};

export const STATUS_ENCAMINHAMENTO = [
  'pendente',
  'agendado',
  'realizado',
  'cancelado',
  'nao_compareceu',
] as const;
export type StatusEncaminhamento = (typeof STATUS_ENCAMINHAMENTO)[number];

export const ROTULO_STATUS_ENCAMINHAMENTO: Record<StatusEncaminhamento, string> = {
  pendente: 'Pendente',
  agendado: 'Agendado',
  realizado: 'Realizado',
  cancelado: 'Cancelado',
  nao_compareceu: 'Não compareceu',
};

export const encaminhamentoSchema = z
  .object({
    tipo: z.enum(TIPOS_ENCAMINHAMENTO, { message: 'Escolha a especialidade.' }),
    descricao: textoOpcional(500),
    dataSolicitacao: dataPassada('A data da solicitação'),
  })
  .refine((e) => e.tipo !== 'outro' || Boolean(e.descricao), {
    path: ['descricao'],
    message: 'Descreva o encaminhamento.',
  });
export type DadosEncaminhamento = z.infer<typeof encaminhamentoSchema>;

/** Andamento do encaminhamento: agendado → realizado (com resultado), cancelado ou não compareceu. */
export const andamentoEncaminhamentoSchema = z
  .object({
    status: z.enum(['agendado', 'realizado', 'cancelado', 'nao_compareceu']),
    dataAgendada: z.iso.date({ message: 'Informe uma data válida.' }).nullable().optional(),
    dataRealizacao: dataPassada('A data da realização').nullable().optional(),
    resultado: z.enum(['normal', 'alterado', 'inconclusivo']).nullable().optional(),
    resultadoDescricao: textoOpcional(),
    motivoCancelamento: textoOpcional(1000),
  })
  .refine((a) => a.status !== 'agendado' || Boolean(a.dataAgendada), {
    path: ['dataAgendada'],
    message: 'Informe a data agendada.',
  })
  .refine((a) => a.status !== 'realizado' || Boolean(a.dataRealizacao), {
    path: ['dataRealizacao'],
    message: 'Informe a data da realização.',
  })
  .refine((a) => a.status !== 'realizado' || Boolean(a.resultado), {
    path: ['resultado'],
    message: 'Informe o resultado da avaliação.',
  })
  .refine((a) => a.status !== 'cancelado' || (a.motivoCancelamento?.length ?? 0) >= 10, {
    path: ['motivoCancelamento'],
    message: 'Explique o cancelamento (mínimo de 10 caracteres).',
  });
export type DadosAndamentoEncaminhamento = z.infer<typeof andamentoEncaminhamentoSchema>;

// ---------------------------------------------------------------- agenda e encerramento
/** D-041: cancelar uma coleta ou consulta exige justificativa (a de 12 meses inclusive, §6.5). */
export const cancelarItemSchema = z.object({
  motivo: justificativa('Explique o cancelamento'),
});
export type DadosCancelarItem = z.infer<typeof cancelarItemSchema>;

/** D-044: o médico encerra; divergir da sugestão (ou encerrar sem sugestão) exige justificativa. */
export const encerrarSeguimentoSchema = z.object({
  classificacao: z.enum(CLASSIFICACOES_SEGUIMENTO, { message: 'Escolha a classificação final.' }),
  dataEncerramento: dataPassada('A data do encerramento'),
  justificativa: textoOpcional(1000),
});
export type DadosEncerrarSeguimento = z.infer<typeof encerrarSeguimentoSchema>;

/** A justificativa é exigida quando a classificação difere da sugerida (a API confere). */
export const encerramentoExigeJustificativa = (
  classificacao: string,
  sugerida: string | null,
): boolean => classificacao !== sugerida;
